import React, { useState, useEffect, useContext } from "react";
import {
  MdImage,
  MdThumbUp,
  MdThumbDown,
  MdChatBubbleOutline,
  MdBlock,
  MdFlag,
  MdClose,
  MdSend,
} from "react-icons/md"; // Import icons for actions
import { UserContext } from "../context/UserContext";
import axios from "axios";

function DiscussionForum({ selectedBill }) {
  const { emailId, firstName } = useContext(UserContext); // Access emailId and firstName from context
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState([]);
  const [wordCount, setWordCount] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const [activeReply, setActiveReply] = useState(null);
  const [newReply, setNewReply] = useState("");
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [blockPopup, setBlockPopup] = useState({ visible: false, username: "", blockedEmail: "" });
  const [reportPopup, setReportPopup] = useState({ visible: false, username: "", reportedEmail: "", reason: "" });
  const [blockedEmailId, setBlockedEmailId] = useState(""); // <-- New state

  // Fetch comments whenever selectedBill changes
  useEffect(() => {
    if (selectedBill) {
      fetchComments();
    }
  }, [selectedBill]);

  // Fetch comments from the server
  const fetchComments = async () => {
    try {
      // Fetch main comments
      const response = await axios.get(
        `/api/bills/${encodeURIComponent(selectedBill)}/comments`,
        { params: { emailId } }
      );

      if (response.status === 200 && Array.isArray(response.data)) {
        console.log("Fetched comments data:", response.data); // Debug log

        const comments = response.data.map((comment) => {
          console.log("Individual comment data:", comment); // Debug log
          return {
            Comment_Id: comment.Comment_Id,
            Username: comment.Username,
            emailId: comment.Email_Id || "",
            Datestamp: comment.Datestamp,
            Timestamp: comment.Timestamp,
            comments: comment.comments,
            media: comment.media || null,
            Likes: parseInt(comment.Likes) || 0,    // Ensure number type
            Dislikes: parseInt(comment.Dislikes) || 0, // Ensure number type
            userAction: comment.userAction || null,
            replies: comment.replies || [],
          };
        });

        // Debug log final processed comments
        console.log("Processed comments:", comments);

        setComments(comments);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const handleNewCommentChange = (e) => {
    const wordLimit = 400;
    const words = e.target.value.trim().split(/\s+/);

    if (words.length > wordLimit) {
      setNewComment(words.slice(0, wordLimit).join(" "));
      setWordCount(wordLimit);
    } else {
      setNewComment(e.target.value);
      setWordCount(words.length);
    }
  };

  const handleAddComment = async () => {
    if (!emailId) {
      alert("Please log in to add a comment");
      return;
    }

    if (newComment.trim() === "" && !selectedImage) {
      return;
    }

    const currentDate = new Date();
    const formData = new FormData();
    formData.append("emailId", emailId);
    formData.append("username", firstName);
    formData.append("comments", newComment);
    formData.append("billTitle", selectedBill);
    formData.append("date", currentDate.toISOString().split("T")[0]);
    formData.append("time", currentDate.toTimeString().split(" ")[0]);
    if (selectedImage) {
      formData.append("image", selectedImage);
    }

    try {
      const response = await axios.post(
        `/api/bills/${encodeURIComponent(
          selectedBill
        )}/comments`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        const newCommentObj = {
          Comment_Id: Date.now(),
          Username: firstName,
          Datestamp: currentDate.toISOString().split("T")[0],
          Timestamp: currentDate.toTimeString().split(" ")[0],
          comments: newComment,
          Likes: 0,
          Dislikes: 0,
          userAction: null, // New comment has no user action yet
          media: selectedImage ? `/uploads/${response.data.fileName}` : null,
          replies: [],
        };

        setComments([newCommentObj, ...comments]);
        setNewComment("");
        setSelectedImage(null);
        setWordCount(0);
      }
    } catch (error) {
      console.error("Error saving comment:", error);
    }
  };

  const handleCancel = () => {
    setNewComment("");
    setSelectedImage(null);
    setWordCount(0);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedFileTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "image/gif",
      ];
      const maxSize = 2 * 1024 * 1024;

      if (allowedFileTypes.includes(file.type) && file.size <= maxSize) {
        setSelectedImage(file);
      } else if (file.size > maxSize) {
        alert("Maximum size limit for uploading an image is 2MB.");
      } else {
        alert("Please select a valid image file (png, jpg, jpeg, gif).");
      }
    }
  };

  // Update handleLike function
  const handleLike = async (commentId) => {
    if (!emailId) {
      alert("Please log in to like comments");
      return;
    }

    // First update local state for immediate feedback
    setComments((prev) =>
      prev.map((comment) => {
        if (comment.Comment_Id === commentId) {
          let newLikes = comment.Likes;
          let newDislikes = comment.Dislikes;
          let newAction = comment.userAction;

          if (comment.userAction === "like") {
            // Undo like
            newLikes = Math.max(0, newLikes - 1);
            newAction = null;
          } else if (comment.userAction === "dislike") {
            // Switch from dislike to like
            newLikes = newLikes + 1;
            newDislikes = Math.max(0, newDislikes - 1);
            newAction = "like";
          } else {
            // New like
            newLikes = newLikes + 1;
            newAction = "like";
          }

          return {
            ...comment,
            Likes: newLikes,
            Dislikes: newDislikes,
            userAction: newAction
          };
        }
        return comment;
      })
    );

    // Then update the database
    try {
      const response = await axios.post("/api/comments/reaction", {
        emailId: emailId,
        reactionType: "like",
        targetId: commentId,
        targetType: "comment"
      });

      if (response.status !== 200) {
        // If server update fails, revert the local state
        setComments((prev) =>
          prev.map((comment) => {
            if (comment.Comment_Id === commentId) {
              return {
                ...comment,
                Likes: response.data.likes,
                Dislikes: response.data.dislikes,
                userAction: response.data.userAction
              };
            }
            return comment;
          })
        );
      }
    } catch (error) {
      console.error("Error updating like:", error);
      alert("Failed to update like. Please try again.");
    }
  };

  // Update handleDislike function
  const handleDislike = async (commentId) => {
    if (!emailId) {
      alert("Please log in to dislike comments");
      return;
    }

    // First update local state for immediate feedback
    setComments((prev) =>
      prev.map((comment) => {
        if (comment.Comment_Id === commentId) {
          let newLikes = comment.Likes;
          let newDislikes = comment.Dislikes;
          let newAction = comment.userAction;

          if (comment.userAction === "dislike") {
            // Undo dislike
            newDislikes = Math.max(0, newDislikes - 1);
            newAction = null;
          } else if (comment.userAction === "like") {
            // Switch from like to dislike
            newLikes = Math.max(0, newLikes - 1);
            newDislikes = newDislikes + 1;
            newAction = "dislike";
          } else {
            // New dislike
            newDislikes = newDislikes + 1;
            newAction = "dislike";
          }

          return {
            ...comment,
            Likes: newLikes,
            Dislikes: newDislikes,
            userAction: newAction
          };
        }
        return comment;
      })
    );

    // Then update the database
    try {
      const response = await axios.post("/api/comments/reaction", {
        emailId: emailId,
        reactionType: "dislike",
        targetId: commentId,
        targetType: "comment"
      });

      if (response.status !== 200) {
        // If server update fails, revert the local state
        setComments((prev) =>
          prev.map((comment) => {
            if (comment.Comment_Id === commentId) {
              return {
                ...comment,
                Likes: response.data.likes,
                Dislikes: response.data.dislikes,
                userAction: response.data.userAction
              };
            }
            return comment;
          })
        );
      }
    } catch (error) {
      console.error("Error updating dislike:", error);
      alert("Failed to update dislike. Please try again.");
    }
  };

  const handleBlockUser = async () => {
    // Debugging: Log email values to check if they are missing
    console.log("Blocker Email:", emailId);
    console.log("Blocked Email:", blockedEmailId);
  
    // Ensure both emails are present
    if (!emailId || !blockedEmailId) {
      alert("Error: Missing user information.");
      return;
    }
  
    try {
      const response = await axios.post("/api/block-user", {
        blockerEmail: emailId, // ✅ Ensure Blocker Email is sent
        blockedEmail: blockedEmailId, // ✅ Ensure Blocked Email is sent
      });
  
      if (response.status === 200) {
        alert(`User ${blockPopup.username} (${blockedEmailId}) has been blocked successfully.`);
  
        // ✅ Remove blocked user's comments from the UI
        setComments((prev) => prev.filter((comment) => comment.emailId !== blockedEmailId));
  
        // ✅ Close the block popup
        setBlockPopup({ visible: false, username: "", blockedEmail: "" });
        setBlockedEmailId(""); // ✅ Reset after blocking
      }
    } catch (error) {
      console.error("Error blocking user:", error);
      alert("Failed to block user. Please try again.");
    }
  };
  
  const handleReportUser = async () => {
    if (!reportPopup.reason.trim()) {
      alert("Please provide a reason for reporting.");
      return;
    }
  
    try {
      const response = await axios.post("/api/report-user", {
        reporterEmail: emailId, // User A's email
        reportedEmail: reportPopup.reportedEmail, // User B's email
        reason: reportPopup.reason, // The reason provided
      });
  
      if (response.status === 200) {
        alert(`Report against ${reportPopup.username} has been successfully sent.`);
        setReportPopup({ visible: false, username: "", reportedEmail: "", reporterEmail: "", reason: "" });
      }
    } catch (error) {
      console.error("Error submitting report:", error.message);
      alert("Failed to send report. Please try again.");
    }
  };

  const openReplyBox = (commentId) => {
    setActiveReply(commentId);
    setShowReplyBox(true);
  };

  const handleAddReply = async (commentId) => {
    if (newReply.trim() === "") {
      alert("Reply cannot be empty");
      return;
    }

    const currentDate = new Date();
    const replyData = {
      emailId: emailId,
      username: firstName,
      text: newReply,
      date: currentDate.toISOString().split("T")[0],
      time: currentDate.toTimeString().split(" ")[0],
    };

    try {
      const response = await axios.post(
        `/api/comments/${commentId}/replies`,
        replyData
      );

      if (response.status === 201) {
        const newReplyObj = {
          Comment_Id: Date.now(),
          username: firstName,
          date: currentDate.toISOString().split("T")[0],
          time: currentDate.toTimeString().split(" ")[0],
          text: newReply,
          Likes: 0,
          Dislikes: 0,
        };

        setComments((prev) =>
          prev.map((comment) =>
            comment.Comment_Id === commentId
              ? { ...comment, replies: [newReplyObj, ...comment.replies] }
              : comment
          )
        );
        setNewReply("");
        setActiveReply(null);
        setShowReplyBox(false);
      }
    } catch (error) {
      console.error("Error adding reply:", error);
      alert("Failed to add reply. Please try again.");
    }
  };

  const handleCancelReply = () => {
    setNewReply("");
    setActiveReply(null);
    setShowReplyBox(false);
  };

  const generateAvatar = (name) => {
    if (!name) name = "U";
    const firstLetter = name.charAt(0).toUpperCase();
    const colors = ["#FFD700", "#ADFF2F", "#FF4500", "#1E90FF", "#DA70D6"];
    const color = colors[firstLetter.charCodeAt(0) % colors.length];

    return (
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          backgroundColor: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: "20px",
          fontWeight: "bold",
        }}
      >
        {firstLetter}
      </div>
    );
  };

  return (
    <div className="container">
      <div className="comments-section" style={{ marginTop: "10px" }}>
        {/* Add Comment Section */}
        <div
          className="add-comment"
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <div className="user-avatar">{generateAvatar(firstName || "U")}</div>
          <div
            className="comment-input"
            style={{ flex: 1, position: "relative" }}
          >
            <textarea
              className="form-control"
              placeholder="Add a comment..."
              value={newComment}
              onChange={handleNewCommentChange}
              style={{
                width: "100%",
                border: "none",
                borderBottom: "1px solid #ccc",
                outline: "none",
              }}
              rows="1"
            />
            <div
              style={{
                position: "absolute",
                right: "10px",
                bottom: "10px",
                fontSize: "12px",
                color: "#999",
              }}
            >
              {wordCount}/400
            </div>
          </div>
          <button 
            className="btn btn-light"
            onClick={handleCancel}
            style={{ 
              padding: '8px',
              minWidth: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%'
            }}
          >
            <MdClose size={20} />
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleAddComment}
            style={{ 
              padding: '8px',
              minWidth: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%'
            }}
          >
            <MdSend size={20} />
          </button>
        </div>

        {/* Comments List */}
        <div
          className="comments-list"
          style={{
            marginTop: "10px",
            height: `calc(93vh - 60vh)`, // Changed from 65vh to 60vh to make the box taller
            overflowY: "scroll",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            padding: "10px",
            borderRadius: "5px",
          }}
        >
          {comments.length === 0 ? (
            <p>No comments yet.</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.Comment_Id} style={{ marginBottom: "20px" }}>
                {/* Comment */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: "10px",
                  }}
                >
                  <div style={{ marginRight: "10px" }}>
                    {generateAvatar(comment.Username)}
                  </div>
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <strong>@{comment.Username}</strong>
                      {/* <span style={{ fontSize: "12px", color: "#777" }}>({comment.emailId})</span>  {/* ✅ New Line: Display Email */} 
                      <span style={{ fontSize: "12px", color: "#777" }}>
                        {comment.Datestamp} {comment.Timestamp}
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <MdFlag
                        title="Report"
                        style={{ fontSize: "20px", color: "red", cursor: "pointer" }}
                        onClick={() => {
                          console.log("MdFlag Clicked - User B Email:", comment.emailId); // Debugging log ✅
                          
                          if (!comment.emailId) {
                            alert("Error: Reported user's email ID is missing.");
                            return;
                          }

                          setReportPopup({
                            visible: true,
                            username: comment.Username, // User B's username
                            reportedEmail: comment.emailId, // ✅ Set User B's email correctly
                            reporterEmail: emailId, // ✅ User A's email
                            reason: "", // Start with empty reason
                          });
                        }}
                      />
                      <MdBlock
                        title="Block User"
                        style={{ fontSize: "20px", color: "#106cfc", cursor: "pointer" }}
                        onClick={() => {
                          console.log("Blocking User:", comment.Username, "Email:", comment.emailId); // Debugging log ✅
                          
                          if (!comment.emailId) {
                            alert("Error: Blocked user's email ID is missing.");
                            return;
                          }

                          // ✅ Store blocked email in state
                          setBlockedEmailId(comment.emailId);

                          // ✅ Pass both emails through `blockPopup` (same as Report logic)
                          setBlockPopup({
                            visible: true,
                            username: comment.Username,
                            blockedEmail: comment.emailId,
                            blockerEmail: emailId, // ✅ Store the blocker email here
                          });
                        }}
                      />
                      </div>
                    </div>
                    <p style={{ margin: "5px 0" }}>{comment.comments}</p>
                  </div>
                </div>

                {/* Comment Actions */}
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <button
                      onClick={() => handleLike(comment.Comment_Id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid #106cfc",
                        borderRadius: "50%",
                        width: "40px",
                        height: "40px",
                        backgroundColor: comment.userAction === "like" ? "#106cfc" : "transparent",
                        color: comment.userAction === "like" ? "white" : "#106cfc",
                        cursor: "pointer",
                      }}
                    >
                      <MdThumbUp style={{ fontSize: "20px" }} />
                    </button>
                    <span style={{ 
                      fontSize: "14px", 
                      color: "#666",
                      minWidth: "20px",
                      fontWeight: "500"
                    }}>
                      {console.log("Like count for comment:", comment.Likes)} {/* Debug log */}
                      {Number(comment.Likes) || 0}
                    </span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <button
                      onClick={() => handleDislike(comment.Comment_Id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid #106cfc",
                        borderRadius: "50%",
                        width: "40px",
                        height: "40px",
                        backgroundColor: comment.userAction === "dislike" ? "#106cfc" : "transparent",
                        color: comment.userAction === "dislike" ? "white" : "#106cfc",
                        cursor: "pointer",
                      }}
                    >
                      <MdThumbDown style={{ fontSize: "20px" }} />
                    </button>
                    <span style={{ 
                      fontSize: "14px", 
                      color: "#666",
                      minWidth: "20px",
                      fontWeight: "500"
                    }}>
                      {console.log("Dislike count for comment:", comment.Dislikes)} {/* Debug log */}
                      {Number(comment.Dislikes) || 0}
                    </span>
                  </div>

                  <button
                    onClick={() => openReplyBox(comment.Comment_Id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                    }}
                  >
                    <MdChatBubbleOutline
                      style={{ color: "#106cfc", fontSize: "20px" }}
                    />{" "}
                    Reply
                  </button>
                </div>

                {/* Replies TODO fetching */}
                {comment.replies.length > 0 && (
                  <div
                    style={{
                      marginTop: "10px",
                      paddingLeft: "20px",
                      borderLeft: "2px solid #ccc",
                    }}
                  >
                    {comment.replies.map((reply) => (
                      <div
                        key={reply.Recomment_Id}
                        style={{ marginBottom: "10px" }}
                      >
                        <div
                          style={{ display: "flex", alignItems: "flex-start" }}
                        >
                          <div style={{ marginRight: "10px" }}>
                            {generateAvatar(reply.Username)}
                          </div>
                          <div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <strong>@{reply.Username}</strong>
                              <span style={{ fontSize: "12px", color: "#777" }}>
                                {new Date(reply.Datestamp).toLocaleDateString()}{" "}
                                {reply.Timestamp}
                              </span>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <MdFlag
                                title="Report"
                                style={{ fontSize: "20px", color: "red", cursor: "pointer" }}
                                onClick={() =>
                                  setReportPopup({ visible: true, username: comment.Username, reportedEmail: comment.emailId, reason: "" })
                                }
                              />
                              <MdBlock
                              title="Ban User"
                              style={{ fontSize: "20px", color: "#106cfc", cursor: "pointer" }}
                              onClick={() => {
                                console.log("Blocking User:", comment.Username, "Email:", comment.emailId); // Debugging log
                                if (!comment.emailId) {
                                  alert("Error: Blocked user's email ID is missing.");
                                  return;
                                }
                                setBlockedEmailId(comment.emailId); // ✅ Store blocked email
                                setBlockPopup({
                                  visible: true,
                                  username: comment.Username,
                                  blockedEmail: comment.emailId,
                                });
                              }}
                            />
                            </div>
                            </div>
                            <p style={{ margin: "5px 0" }}>{reply.comments}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Reply Box */}
        {showReplyBox && activeReply && (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "10px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            }}
          >
            <h4>Reply to Comment</h4>
            <textarea
              value={newReply}
              onChange={(e) => setNewReply(e.target.value)}
              style={{ width: "100%", marginBottom: "10px" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="btn btn-light"
                onClick={handleCancelReply}
                style={{ marginRight: "10px" }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleAddReply(activeReply)}
              >
                Reply
              </button>
            </div>
          </div>
        )}

      {/* Block Confirmation Popup */}
      {blockPopup.visible && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <h4>Are you sure you want to block {blockPopup.username}?</h4>
          {/* <p><strong>Your Email (Blocker):</strong> {emailId}</p> {/* ✅ Display Blocker's Email */}
          {/* <p><strong>Blocked User Email:</strong> {blockPopup.blockedEmail}</p> ✅ Display Blocked User's Email */}
          <div style={{ marginTop: "10px", display: "flex", justifyContent: "center", gap: "10px" }}>
            <button className="btn btn-light" onClick={() => setBlockPopup({ visible: false, username: "", blockedEmail: "" })}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleBlockUser}>
              Block
            </button>
          </div>
        </div>
      )}

{reportPopup.visible && (
  <div
    style={{
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "#fff",
      padding: "20px",
      borderRadius: "10px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      textAlign: "center",
      width: "350px",
    }}
  >
    <h4>Report {reportPopup.username} to the UrVote Admin?</h4>
    {/* <p><strong>Your Email:</strong> {reportPopup.reporterEmail}</p> ✅ Display User A's Email */}
    {/* <p><strong>Reported User Email:</strong> {reportPopup.reportedEmail}</p> ✅ New Line: Display User B's Email */}
    <p>Provide an explanation for reporting:</p>
    <textarea
      placeholder="Enter your reason here..."
      value={reportPopup.reason}
      onChange={(e) =>
        setReportPopup((prev) => ({
          ...prev,
          reason: e.target.value,
        }))
      }
      style={{
        width: "100%",
        height: "80px",
        padding: "5px",
        border: "1px solid #ccc",
        borderRadius: "5px",
        marginBottom: "10px",
      }}
    />
    <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
      <button
        className="btn btn-light"
        onClick={() =>
          setReportPopup({
            visible: false,
            username: "",
            reportedEmail: "",
            reporterEmail: "",
            reason: "",
          })
        }
      >
        Cancel
      </button>
      <button
        className="btn btn-danger"
        onClick={handleReportUser}
        disabled={!reportPopup.reason.trim()}
      >
        Report
      </button>
    </div>
  </div>
)}

      </div>
    </div>
  );
}

export default DiscussionForum;
